/**
 * The app's languages (phase A of the owner's expansion plan, 2026-09-29).
 *
 * Georgian is the source: its dictionary defines every key, and each other
 * language is typed `Record<Key, Entry>`, so a key missing in one language is
 * a compile error, not a blank on a cashier's screen. The language is chosen
 * per DEVICE (localStorage `lang`), like the palette and the density: at a
 * shared till the cashier may work in Russian while the owner reads Georgian
 * on his phone.
 *
 * What stays Georgian whatever is chosen: the receipt and the Z-report a
 * customer or the tax office reads, RS.GE documents, the accountant's Excel,
 * the legal terms — those are not interface.
 */

export const LANGS = ['ka', 'ru'] as const
export type Lang = (typeof LANGS)[number]

/** Each language's own name — what the picker shows, whatever is chosen. */
export const LANG_NAMES: Record<Lang, string> = { ka: 'ქართული', ru: 'Русский' }

/**
 * A string, or its forms by number when the words change with it — Russian
 * „1 товар, 2 товара, 5 товаров" (Intl.PluralRules picks the form). The
 * number is the `{n}` parameter, or the one `by` names. Georgian nouns do not
 * change with a number, so Georgian entries are plain strings.
 */
export type Entry = string | { one?: string; few?: string; many?: string; other: string; by?: string }

/** A missing value (undefined / null) leaves its `{name}` showing rather than print „undefined". */
export type Params = Record<string, string | number | null | undefined>

const STORAGE_KEY = 'lang'

export function isLang(v: unknown): v is Lang {
  return typeof v === 'string' && (LANGS as readonly string[]).includes(v)
}

function readStored(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return isLang(v) ? v : 'ka'
  } catch {
    return 'ka'
  }
}

let current: Lang = typeof window === 'undefined' ? 'ka' : readStored()
// index.html's boot script sets it before first paint; this keeps the two in
// step if a stored value was refused by `isLang`.
if (typeof document !== 'undefined') document.documentElement.lang = current
const listeners = new Set<() => void>()

export function getLang(): Lang {
  return current
}

export function setLang(lang: Lang): void {
  if (lang === current) return
  current = lang
  try { localStorage.setItem(STORAGE_KEY, lang) } catch { /* private window: this session only */ }
  if (typeof document !== 'undefined') document.documentElement.lang = lang
  for (const l of listeners) l()
}

export function subscribeLang(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

const plural = new Map<Lang, Intl.PluralRules>()

/** One entry in one language, its `{name}` placeholders filled. */
export function format(lang: Lang, entry: Entry, params?: Params): string {
  let s: string
  if (typeof entry === 'string') {
    s = entry
  } else {
    const n = Number(params?.[entry.by ?? 'n'] ?? 0)
    let rules = plural.get(lang)
    if (!rules) { rules = new Intl.PluralRules(lang); plural.set(lang, rules) }
    const form = rules.select(n) as 'one' | 'few' | 'many' | 'other'
    s = entry[form] ?? entry.other
  }
  if (!params) return s
  return s.replace(/\{(\w+)\}/g, (m, k: string) => (params[k] != null ? String(params[k]) : m))
}

/** The `{name}` placeholders an entry uses — the test holds every language to the same set. */
export function placeholders(entry: Entry): string[] {
  const texts = typeof entry === 'string' ? [entry] : [entry.one, entry.few, entry.many, entry.other].filter((x): x is string => typeof x === 'string')
  const out = new Set<string>()
  for (const s of texts) for (const m of s.matchAll(/\{(\w+)\}/g)) out.add(m[1])
  return [...out].sort()
}
